import { PDFViewer, pdf } from '@react-pdf/renderer';
import { Dispatch, SetStateAction, useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';
import { Option } from '../../../../../../types/Option';

import { META_COLLECT_API } from '../../../../../../api';

import { LoadingData } from '../../../../../../components/LoadingData';
import { ModalInfoTemplete } from '../../../../../../components/Modal/ModalInfoTemplete';
import { ContainerItemModal } from '../../../../../../components/Modal/ModalInfoTemplete/styles';
import { PageHeader, ViewMode } from '../../../../../../components/PageHeader';
import { useSigninContext } from '../../../../../../context/AuthProviderPages/Signin';
import {
  ContainerPage,
  PageContent,
} from '../../../../../../styles/global.styles';
import { PQFLVisitRow } from '../../page';
import { PQFLDocument } from '../pdf';
import { formatISODate } from '../table';
import * as S from './styled';
import { processImage, getAnswerValue } from './utils';

type ContentWrapperProps = {
  loading: boolean;
  row: PQFLVisitRow | null;
  openModal: boolean;
  handleCloseModal: () => void;
  children: React.ReactNode;
  modifyViewMode?: ViewMode;
  modalType: 'visualize' | 'print' | 'print-with-photos' | null;
  setModifyViewMode?: Dispatch<SetStateAction<ViewMode>>;
  pageOption: Option['option'];
};

type QualityItem = {
  DFSIGLAANALISE: string;
  DFMEDIA: number;
  DFMEDIATRIMESTRE: number;
  DFUNIDADE?: string;
};

export const ContentWrapper = ({
  loading,
  row: question,
  openModal,
  handleCloseModal,
  children,
  modifyViewMode,
  modalType,
  setModifyViewMode,
  pageOption,
}: ContentWrapperProps) => {
  const { user, path } = useSigninContext();
  // estados para monitorar o carregamento da logo, da assinatura do produtor, da assinatura do técnico e dos dados de qualidade, respectivamente
  const [finalLogo, setFinalLogo] = useState<string | undefined>(undefined);
  const [finalSignature, setFinalSignature] = useState<string | undefined>(
    undefined,
  );
  const [finalTechSignature, setFinalTechSignature] = useState<
    string | undefined
  >(undefined);
  const [qualityData, setQualityData] = useState<QualityItem[]>([]);
  const [isFetchingPhotos, setIsFetchingPhotos] = useState<boolean>(false);

  const isPrintMode =
    modalType === 'print' || modalType === 'print-with-photos';

  // estado que monitora a abertura do modal e dispara a busca dos dados na api
  useEffect(() => {
    const fetchQuality = async () => {
      if (openModal && question?.propriedade?.id && question?.data_final) {
        try {
          const { data: response } = await META_COLLECT_API.get(
            `/pqfl/qualidade/visita-qualidade`,
            {
              params: {
                idPropriedade: question.propriedade.id,
                dataFinal: question.data_final,
              },
              headers: { path },
            },
          );
          const formatted = (response.data || []).map((item: QualityItem) => ({
            ...item,
            DFMEDIA: item.DFMEDIA
              ? String(item.DFMEDIA).substring(0, 5)
              : item.DFMEDIA,
          }));
          setQualityData(formatted);
        } catch (error) {
          toast.error('Erro ao buscar dados da qualidade para o PDF');
        }
      }
    };

    const fetchPhotos = async () => {
      if (!openModal || !question || modalType !== 'print-with-photos') return;

      const itemsToFetch = question.respostas
        .flatMap(r => r.itens)
        .filter(item => item.possui_foto && !item.foto_base64);

      if (itemsToFetch.length === 0) return;

      setIsFetchingPhotos(true);
      try {
        await Promise.all(
          itemsToFetch.map(async item => {
            try {
              const { data: response } = await META_COLLECT_API.get(
                `/pqfl/visits/item/${item.id}/photo`,
                { headers: { path } },
              );
              item.foto_base64 = response.data.foto_base64;
            } catch (err) {
              console.error(`Erro ao buscar foto do item ${item.id}`, err);
            }
          }),
        );
      } catch (error) {
        toast.error('Erro ao buscar fotos da visita');
      } finally {
        setIsFetchingPhotos(false);
      }
    };

    fetchQuality();
    fetchPhotos();
  }, [openModal, question?.id, path, modalType]);

  useEffect(() => {
    if (openModal && question?.id) {
      if (isPrintMode) {
        processImage(user?.DFLOGOEMPRESA).then(setFinalLogo);
      }
      processImage(question.assinatura_produtor).then(setFinalSignature);
      processImage(question.assinatura_tecnico).then(setFinalTechSignature);
    }
  }, [
    isPrintMode,
    openModal,
    question?.id,
    user?.DFLOGOEMPRESA,
    question?.assinatura_produtor,
    question?.assinatura_tecnico,
  ]);

  const handleOpenNewTab = async () => {
    if (!question) return;
    const doc = (
      <PQFLDocument
        base64Data={finalLogo}
        assinatura_produtor={finalSignature}
        assinatura_tecnico={finalTechSignature}
        username={user?.DFLOGINUSUARIO}
        company={{ legalName: question.unidade.nome }}
        answers={question.respostas.filter(r => r.status !== 'C')}
        qualityItems={qualityData}
        property={{
          name: question.propriedade.nome_propriedade,
          producerName: question.propriedade.nome_produtor,
          municipalityName: question.propriedade.nome_municipio,
        }}
        technicalRecommendation={question.recomendacao_tecnica}
        endDate={question.data_final}
        status={question.status}
        showPhotos={modalType === 'print-with-photos'}
      />
    );
    const blob = await pdf(doc).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  return (
    <>
      <PageHeader
        pageIcon={{ option: pageOption }}
        pageTitle="PQFL / Visitas"
        hasFilter={false}
        modifyViewMode={modifyViewMode}
        setModifyViewMode={setModifyViewMode}
      />
      <ContainerPage>
        <PageContent
          style={{
            justifyContent: loading === true ? 'center' : 'space-between',
          }}
        >
          {children}
        </PageContent>
        {openModal && question && (
          <>
            {/* MODAL VISUALIZAR */}
            {modalType === 'visualize' && (
              <ModalInfoTemplete
                option={pageOption}
                titleModal={'Visita PQFL'}
                closeModal={handleCloseModal}
                isOpen={openModal}
              >
                <S.RowItens>
                  <ContainerItemModal>
                    <label>Código</label>
                    <div>
                      <span>{question.id}</span>
                    </div>
                  </ContainerItemModal>
                  <ContainerItemModal>
                    <label>Responsável</label>
                    <div>
                      <span>{question.funcionario.nome}</span>
                    </div>
                  </ContainerItemModal>
                </S.RowItens>
                <S.RowItens>
                  <ContainerItemModal>
                    <label>Propriedade</label>
                    <div>
                      <span>{question.propriedade.nome_propriedade}</span>
                    </div>
                  </ContainerItemModal>
                  <ContainerItemModal>
                    <label>Produtor</label>
                    <div>
                      <span>{question.propriedade.nome_produtor}</span>
                    </div>
                  </ContainerItemModal>
                </S.RowItens>
                <S.RowItens>
                  <ContainerItemModal>
                    <label>Início</label>
                    <div>
                      <span>{formatISODate(question.data_inicio)}</span>
                    </div>
                  </ContainerItemModal>
                  <ContainerItemModal>
                    <label>Fim</label>
                    <div>
                      <span>{formatISODate(question.data_final)}</span>
                    </div>
                  </ContainerItemModal>
                </S.RowItens>
                <S.RowItens>
                  <ContainerItemModal>
                    <label>Status</label>
                    <div>
                      <span>{question.status}</span>
                    </div>
                  </ContainerItemModal>
                </S.RowItens>
                <S.RowItensLarge>
                  <ContainerItemModal>
                    <label>Recomendação Técnica</label>
                    <div>
                      <span>
                        {question.recomendacao_tecnica
                          ? question.recomendacao_tecnica
                          : '-'}
                      </span>
                    </div>
                  </ContainerItemModal>
                </S.RowItensLarge>
                {question.respostas
                  .filter(r => r.status !== 'C')
                  .map((resposta, index) => (
                    <Fragment key={index}>
                      <S.RowItens>
                        <h2>{resposta.questionario.descricao}</h2>
                      </S.RowItens>
                      <S.ContainerListPage>
                        <table>
                          <thead style={{ position: 'static' }}>
                            <tr>
                              <th className="first-table-th">Pergunta</th>
                              <th className="list-th">Respostas</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resposta.itens
                              .sort(
                                (a, b) => a.ordem_pergunta - b.ordem_pergunta,
                              )
                              .map((item, i) => (
                                <tr key={i}>
                                  <td className="list-td-small">
                                    {item.grau1_pergunta}.{item.grau2_pergunta}{' '}
                                    {item.texto_pergunta}{' '}
                                    {item.observacao_pergunta && (
                                      <div
                                        style={{
                                          fontSize: '0.85em',
                                          color: '#666',
                                          fontStyle: 'italic',
                                          marginTop: '4px',
                                        }}
                                      >
                                        Nota: {item.observacao_pergunta}
                                      </div>
                                    )}
                                  </td>
                                  <td className="list-td-large">
                                    {getAnswerValue(item)}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </S.ContainerListPage>
                    </Fragment>
                  ))}
              </ModalInfoTemplete>
            )}
            {isPrintMode && (
              <ModalInfoTemplete
                option={pageOption}
                titleModal={
                  <S.TitleWrapper>
                    <span>Relatório de Visita PQFL</span>
                  </S.TitleWrapper>
                }
                closeModal={handleCloseModal}
                isOpen={openModal}
                headerAction={
                  !isFetchingPhotos && (
                    <S.ActionButton onClick={handleOpenNewTab}>
                      📄 Abrir em Nova Aba
                    </S.ActionButton>
                  )
                }
              >
                <div style={{ width: '100%', height: '95vh' }}>
                  {isFetchingPhotos ? (
                    <LoadingData title="Carregando fotos..." />
                  ) : (
                    <PDFViewer
                      style={{ width: '100%', height: '100%', border: 'none' }}
                    >
                      <PQFLDocument
                        base64Data={finalLogo}
                        assinatura_produtor={finalSignature}
                        assinatura_tecnico={finalTechSignature}
                        username={user?.DFLOGINUSUARIO}
                        company={{ legalName: question.unidade.nome }}
                        answers={question.respostas.filter(
                          r => r.status !== 'C',
                        )}
                        qualityItems={qualityData}
                        property={{
                          name: question.propriedade.nome_propriedade,
                          producerName: question.propriedade.nome_produtor,
                          municipalityName: question.propriedade.nome_municipio,
                        }}
                        technicalRecommendation={question.recomendacao_tecnica}
                        endDate={question.data_final}
                        status={question.status}
                        showPhotos={modalType === 'print-with-photos'}
                      />
                    </PDFViewer>
                  )}
                </div>
              </ModalInfoTemplete>
            )}
          </>
        )}
      </ContainerPage>
    </>
  );
};
